/**
 * Druid kit identity (plan 29 F9, lane CB 2026-10-11).
 *
 * Before this lane the druid's casts were school defaults: the auto, Wrath
 * and Moonfire muzzled the hunter's `ranged_projectile` puff (map rows),
 * Wrath and Entangling Roots wound up 1.5 s behind the generic casting glow,
 * Healing Touch wore the paladin's gold `holy_heal`, Mark of the Wild the
 * warrior's `defensive_aura`, Barkskin's TOML named an unregistered id and
 * Rejuvenation a `regrowth` it shares with Regrowth; Entangling Roots' 4 s
 * server root drew a generic pulsing tint; Hurricane hit with `hit_nature`
 * under Blizzard's `snow_fall`. Each def here is named by the ability's TOML
 * (`cast_effect` / `impact_effect`) so the local module, the confirmed impact
 * and a remote replay read the same id. The impacts the K21 lane authored
 * (nature_bolt_impact, wrath_impact, moonfire_impact, roots_impact) and the
 * Barkskin / Mark of the Wild states (DruidStateVFX) are kept.
 *
 * Palette (TOML `aura_color`, ≥ 40 RGB apart inside the kit): auto spring
 * green, Wrath emerald, Moonfire lunar silver-violet, Entangling Roots olive,
 * Rejuvenation mint, Healing Touch verdant gold, Barkskin bark brown, Mark of
 * the Wild amber, Harebound hare-pelt cream. Wind-up tells sit over the
 * sprite or at the feet, outside the body, stroke-heavy at a high alpha (lane
 * BZ: a tell drawn into the body hides behind it). One-shot bursts end; the
 * states are persistent `status` defs mapped in STATUS_PRESENTATIONS. No
 * screen shake or flash (the hit tier owns those).
 */
import type { VFXEffectConfig, VFXPrimitive } from '../../VFXEffectRegistry';
import { ringBlast, shardBurst, glintScatter, starFlash, wispRise } from './burstBuilders';

const SPRING = 0x44cc44;
const SPRING_PALE = 0xccffcc;
const EMERALD = 0x1fa35a;
const EMERALD_PALE = 0x9cf0b8;
const MOON = 0xc8a2e8;
const MOON_SILVER = 0xf2eeff;
const OLIVE = 0x6b8e23;
const SOIL = 0x4a3b1e;
const MINT = 0xa8f0a0;
const MINT_CORE = 0xeaffea;
const VERDANT = 0x9acd32;
const GOLD = 0xffe27a;
const BARK = 0x8b5a2b;
const BARK_PALE = 0xd2b48c;
const AMBER = 0xe8a33d;
const AMBER_PALE = 0xffe0a8;
const GALE = 0xb8f5c8;

/** A pointed leaf, tip up (local units). */
const LEAF = [0, -8, 4, -1, 0, 7, -4, -1];

/** Nature Bolt's hand spark: three leaves flick off the fingers. */
const NATURE_BOLT_SPARK: VFXEffectConfig = {
  id: 'nature_bolt_spark',
  name: 'Nature Bolt Spark',
  category: 'burst',
  description: 'Three spring-green leaves flick off the hand around a pale seed of light as the bolt leaves',
  primitives: [
    { type: 'circle', radius: 4, color: SPRING_PALE, alpha: 1, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2, duration: 160, ease: 'Power2' } },
    shardBurst({ count: 3, spread: 8, color: SPRING, shard: { points: LEAF }, travel: 22, durationMs: 220, depth: 97 }),
  ],
};

/** Wrath's 1.5 s gather round the body: two emerald wind arcs spiral in,
 *  leaves drawn along them, to a bright seed of wrath at the chest (drawn over
 *  the sprite and outside it — over the head it hid under the cast bar). */
const WRATH_GATHER: VFXEffectConfig = {
  id: 'wrath_gather',
  name: 'Wrath Gather',
  category: 'burst',
  description: 'Two emerald wind arcs spiral in round the druid, leaves drawn along them, to a bright seed of wrath at the chest',
  primitives: [
    ...[0, 180].map((start): VFXPrimitive => ({
      type: 'arc', arcRadius: 46, arcStartAngle: start, arcEndAngle: start + 110, width: 3.5, color: EMERALD_PALE, alpha: 0.95, depth: 102,
      tween: { scale: 0.45, rotation: Math.PI * 1.5, duration: 1450, ease: 'Sine.easeIn' },
    })),
    { type: 'polygon', polygonPoints: LEAF, color: EMERALD, alpha: 0.95, depth: 102,
      stroke: { color: EMERALD_PALE, width: 1 },
      scatter: { count: 6, radius: 58 },
      tween: { scale: 0.4, alpha: 1, rotation: Math.PI, duration: 1450, ease: 'Sine.easeIn' } },
    { type: 'circle', radius: 5, color: SPRING_PALE, alpha: 0.9, depth: 103, glow: true,
      tween: { scale: 2.4, alpha: 1, duration: 1450, ease: 'Quad.easeIn' } },
  ],
};

/** Moonfire's call at the hand: a silver crescent flashes, violet dust. */
const MOONFIRE_CALL: VFXEffectConfig = {
  id: 'moonfire_call',
  name: 'Moonfire Call',
  category: 'burst',
  description: 'A silver crescent moon flashes over the raised hand and sheds violet dust — lunar, no green',
  primitives: [
    { type: 'arc', arcRadius: 14, arcStartAngle: 40, arcEndAngle: 320, width: 5, offsetY: -22, color: MOON_SILVER, alpha: 1, depth: 101, glow: true,
      tween: { alpha: 0, scale: 1.6, duration: 260, ease: 'Power2' } },
    starFlash({ points: 4, innerRadius: 2, outerRadius: 8, color: MOON, spinRad: 0.6, durationMs: 200, depth: 97 }),
    glintScatter({ count: 4, spread: 12, color: MOON, durationMs: 220, delayMs: 40 }),
  ],
};

/** Entangling Roots' 1.5 s tell: vines curl up out of the ground at the druid's feet. */
const ROOTS_GATHER: VFXEffectConfig = {
  id: 'roots_gather',
  name: 'Roots Gather',
  category: 'burst',
  description: 'Olive vines curl up out of a ring of soil at the druid\'s feet, moss motes rising, as the roots are called',
  primitives: [
    { type: 'ellipse', width: 70, height: 16, offsetY: 30, color: SOIL, alpha: 0.55, depth: 92,
      stroke: { color: OLIVE, width: 2.5 },
      tween: { scale: 1.25, alpha: 0.7, duration: 1450, ease: 'Sine.easeOut' } },
    ...[-1, 1].map((side): VFXPrimitive => ({
      type: 'arc', arcRadius: 18, arcStartAngle: side < 0 ? 200 : -20, arcEndAngle: side < 0 ? 340 : 160,
      width: 4, offsetX: side * 26, offsetY: 18, color: OLIVE, alpha: 0.95, depth: 101,
      tween: { y: -16, scale: 1.3, duration: 1450, ease: 'Sine.easeOut' },
    })),
    wispRise({ count: 6, spread: 30, radius: 2.5, color: 0x8fbc5a, travel: 34, durationMs: 1400, depth: 101 }),
  ],
};

/** Entangling Roots' state on the target for the server's 4 s root: vines coiled round its legs. */
const ENTANGLING_ROOTS_STATE: VFXEffectConfig = {
  id: 'entangling_roots_state',
  name: 'Entangling Roots State',
  category: 'status',
  description: 'Olive vines wrap round the target\'s legs, thorns standing out of them, over a ring of soil: it is held in place',
  followTarget: 'target',
  primitives: [
    { type: 'ellipse', width: 62, height: 14, offsetY: 26, color: SOIL, alpha: 0.55, depth: 101,
      stroke: { color: OLIVE, width: 3 } },
    // The wraps: a front and a back half-loop round the legs, gently tightening.
    ...[[20, 160, 103], [200, 340, 100]].map(([a0, a1, depth]): VFXPrimitive => ({
      type: 'arc', arcRadius: 22, arcStartAngle: a0, arcEndAngle: a1, width: 4, offsetY: 14,
      color: 0x86a83a, alpha: 0.95, depth,
      tween: { scale: 0.9, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' },
    })),
    ...[-22, -8, 8, 22].map((x, i): VFXPrimitive => ({
      type: 'polygon', polygonPoints: [0, -9, 4, 3, -4, 3], offsetX: x, offsetY: i % 2 ? 8 : 20,
      color: 0xc0dc5a, alpha: 0.95, depth: 103,
    })),
  ],
};

/** Rejuvenation's cast: a ring of mint leaves bursts out and motes rise. */
const REJUVENATION_BLOOM: VFXEffectConfig = {
  id: 'rejuvenation_bloom',
  name: 'Rejuvenation Bloom',
  category: 'burst',
  description: 'A ring of mint leaves bursts out from the body and pale motes rise — a slow heal begins',
  primitives: [
    ringBlast({ radius: 20, color: MINT, alpha: 0.4, stroke: { color: MINT_CORE, width: 2 }, expandTo: 2, durationMs: 420 }),
    shardBurst({ count: 6, spread: 16, color: 0x3fcf7a, shard: { points: LEAF }, travel: 44, durationMs: 460, depth: 101,
      stroke: { color: MINT_CORE, width: 1 } }),
    wispRise({ count: 5, spread: 20, radius: 2.5, color: MINT_CORE, travel: 40, durationMs: 620, delayMs: 80, glow: true, depth: 101 }),
  ],
};

/** Rejuvenation's heal-over-time state: two mint buds pulse at the shoulders, a soft ring at the feet. */
const REJUVENATION_STATE: VFXEffectConfig = {
  id: 'rejuvenation_state',
  name: 'Rejuvenation State',
  category: 'status',
  description: 'Two mint leaf-buds pulse beside the shoulders over a soft mint ring at the feet while the heal ticks',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 56, height: 12, offsetY: 30, color: MINT, alpha: 0.12, depth: 99,
      stroke: { color: MINT, width: 2 },
      tween: { alpha: 0.3, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    ...[-1, 1].map((side): VFXPrimitive => ({
      type: 'polygon', polygonPoints: LEAF, offsetX: side * 30, offsetY: -20, color: MINT, alpha: 0.5, depth: 101,
      stroke: { color: MINT_CORE, width: 1 },
      tween: { alpha: 0.95, scale: 1.25, duration: 1500, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' },
    })),
  ],
};

/** Healing Touch's 1.5 s cast: verdant-gold motes gather into the hands. */
const HEALING_TOUCH_GATHER: VFXEffectConfig = {
  id: 'healing_touch_gather',
  name: 'Healing Touch Gather',
  category: 'burst',
  description: 'Verdant-gold motes drift in from around the druid to a warm glow at the hands as the touch is gathered',
  primitives: [
    { type: 'arc', arcRadius: 40, arcStartAngle: -180, arcEndAngle: 180, width: 2, color: VERDANT, alpha: 0.85, depth: 101,
      tween: { scale: 0.4, alpha: 1, duration: 1450, ease: 'Sine.easeIn' } },
    { type: 'circle', radius: 2.5, color: GOLD, alpha: 0.95, depth: 102, glow: true,
      scatter: { count: 8, radius: 46 },
      tween: { scale: 0.6, alpha: 1, duration: 1450, ease: 'Sine.easeIn' } },
    { type: 'circle', radius: 5, offsetX: 16, color: GOLD, alpha: 0.6, depth: 102, glow: true,
      tween: { scale: 2.2, alpha: 1, duration: 1450, ease: 'Quad.easeIn' } },
  ],
};

/** Healing Touch's landing on whoever it heals: a verdant flower opens. */
const HEALING_TOUCH_BLOOM: VFXEffectConfig = {
  id: 'healing_touch_bloom',
  name: 'Healing Touch Bloom',
  category: 'burst',
  description: 'Six verdant petals open round a gold core on the healed body and gold motes rise — a big, slow heal lands',
  primitives: [
    shardBurst({ count: 6, spread: 10, color: VERDANT, shard: { points: [0, -11, 6, -2, 0, 9, -6, -2] }, travel: 30, durationMs: 560,
      stroke: { color: 0xeaffb0, width: 1 }, depth: 101 }),
    { type: 'circle', radius: 7, color: GOLD, alpha: 1, depth: 102, glow: true,
      tween: { alpha: 0, scale: 2.4, duration: 420, ease: 'Power2' } },
    wispRise({ count: 6, spread: 22, radius: 2.5, color: GOLD, travel: 48, durationMs: 620, delayMs: 100, glow: true, depth: 102 }),
  ],
};

/** Barkskin's cast: bark plates slam onto the body and wood chips fly. */
const BARKSKIN_FORM: VFXEffectConfig = {
  id: 'barkskin_form',
  name: 'Barkskin Form',
  category: 'burst',
  description: 'Six bark plates slam in round the druid with a brown crack-ring and flying wood chips: the skin hardens',
  primitives: [
    { type: 'polygon', polygonPoints: [-5, -11, 4, -9, 6, 0, 4, 10, -4, 11, -6, 2], color: BARK, alpha: 0.95, depth: 101,
      stroke: { color: BARK_PALE, width: 1.5 },
      scatter: { count: 6, radius: 62 },
      tween: { scale: 0.55, alpha: 0, duration: 320, ease: 'Back.easeIn' } },
    ringBlast({ radius: 30, color: BARK, alpha: 0.35, stroke: { color: BARK_PALE, width: 3 }, expandTo: 1.8, durationMs: 360, delayMs: 260 }),
    shardBurst({ count: 6, spread: 26, color: BARK_PALE, shard: { width: 5, height: 2 }, travel: 50, durationMs: 300, delayMs: 270 }),
  ],
};

/** Mark of the Wild's cast: a ring of amber leaves blooms and a wild emblem rises. */
const WILD_MARK_CAST: VFXEffectConfig = {
  id: 'wild_mark_cast',
  name: 'Wild Mark Cast',
  category: 'burst',
  description: 'A ring of amber leaves blooms round the druid and an amber paw-leaf emblem rises over the head',
  primitives: [
    shardBurst({ count: 8, spread: 22, color: AMBER, shard: { points: LEAF }, travel: 52, durationMs: 520, depth: 101 }),
    ringBlast({ radius: 26, color: AMBER, alpha: 0.3, stroke: { color: AMBER_PALE, width: 2 }, expandTo: 1.9, durationMs: 460 }),
    // The emblem: a pad and three toes, rising over the head.
    { type: 'circle', radius: 7, offsetY: -46, color: AMBER, alpha: 0.95, depth: 102, glow: true,
      tween: { y: -18, alpha: 0, duration: 760, delay: 120, ease: 'Sine.easeOut' } },
    ...[-9, 0, 9].map((x): VFXPrimitive => ({
      type: 'circle', radius: 3.2, offsetX: x, offsetY: x === 0 ? -60 : -57, color: AMBER_PALE, alpha: 0.95, depth: 102,
      tween: { y: -18, alpha: 0, duration: 760, delay: 120, ease: 'Sine.easeOut' },
    })),
  ],
};

/** Hurricane's storm for its 10 s channel: wind bands whirl round the druid, leaves torn along them. */
const HURRICANE_STORM: VFXEffectConfig = {
  id: 'hurricane_storm',
  name: 'Hurricane Storm',
  category: 'zone',
  description: 'Pale-green wind bands whirl round the druid with torn leaves along them for the whole channel',
  duration: 10000,
  primitives: [
    { type: 'ellipse', width: 120, height: 34, offsetY: 26, color: GALE, alpha: 0.12, depth: 10,
      stroke: { color: GALE, width: 2 },
      tween: { alpha: 0.24, scale: 1.04, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    ...[0, 1, 2].map((i): VFXPrimitive => ({
      type: 'arc', arcRadius: 34 + i * 16, arcStartAngle: i * 120, arcEndAngle: i * 120 + 200, width: 3 - i * 0.5,
      color: i === 1 ? EMERALD_PALE : GALE, alpha: 0.75 - i * 0.12, depth: 96,
      tween: { rotation: (i % 2 ? -1 : 1) * Math.PI * 2, duration: 900 + i * 250, repeat: -1, ease: 'Linear' },
    })),
    { type: 'polygon', polygonPoints: LEAF, color: SPRING, alpha: 0.9, depth: 97,
      scatter: { count: 7, radius: 58 },
      tween: { rotation: Math.PI * 2, duration: 700, repeat: -1, ease: 'Linear' } },
  ],
};

/** Hurricane's hit, on each body each tick: a gust whorl tears at it. */
const HURRICANE_GUST: VFXEffectConfig = {
  id: 'hurricane_gust',
  name: 'Hurricane Gust',
  category: 'burst',
  description: 'A small pale-green whorl spins over the struck body and two leaves are torn away',
  primitives: [
    { type: 'arc', arcRadius: 12, arcStartAngle: 0, arcEndAngle: 270, width: 3, color: GALE, alpha: 0.95, depth: 97,
      tween: { rotation: Math.PI * 1.5, alpha: 0, scale: 1.6, duration: 300, ease: 'Power1' } },
    shardBurst({ count: 2, spread: 8, color: SPRING, shard: { points: LEAF }, travel: 28, durationMs: 280, depth: 97 }),
  ],
};

export const DRUID_KIT_VFX: VFXEffectConfig[] = [
  NATURE_BOLT_SPARK,
  WRATH_GATHER,
  MOONFIRE_CALL,
  ROOTS_GATHER, ENTANGLING_ROOTS_STATE,
  REJUVENATION_BLOOM, REJUVENATION_STATE,
  HEALING_TOUCH_GATHER, HEALING_TOUCH_BLOOM,
  BARKSKIN_FORM,
  WILD_MARK_CAST,
  HURRICANE_STORM, HURRICANE_GUST,
];
